import { generatePath } from 'react-router';

import { Property } from '~/common/types/buildings';

import { BreadcrumbItem } from '../components/shared/PageBreadcrumbs';
import { supportNeedsBaseUrl } from '../config/appConfig';
import { routes } from '../config/routes';

export const isPcfraRoute = (pathname: string) => pathname.startsWith('/pcfra');

export const getPropertyBreadcrumbs = (
  { name, address, propertyId }: Property,
  withPropertyLink = true,
): BreadcrumbItem[] => [
  { name: 'Home', link: routes.home },
  {
    name: name.trim() || address.split(',')[0].trim(),
    link: withPropertyLink
      ? generatePath(
          isPcfraRoute(window.location.pathname) ? routes.pcfraProperty : routes.property,
          {
            propertyId: String(propertyId),
          },
        )
      : undefined,
  },
];

export const toPropertyPage = (propertyId: ScalarId) =>
  generatePath(routes.property, { propertyId: String(propertyId) });

export const toPersonPage = (propertyId: ScalarId, personId: ScalarId) =>
  generatePath(routes.person, { propertyId: String(propertyId), personId: String(personId) });

export const toPersonEditPage = (propertyId: ScalarId, personId: ScalarId) =>
  generatePath(routes.personEdit, { propertyId: String(propertyId), personId: String(personId) });

export const toHouseholdMemberCreate = (propertyId: ScalarId) =>
  generatePath(routes.householdMemberCreate, { propertyId: String(propertyId) });

export const toContactDetailsEdit = (propertyId: ScalarId, personId: ScalarId) =>
  generatePath(routes.contactDetailsEdit, {
    propertyId: String(propertyId),
    personId: String(personId),
  });

export const toVisitPage = (propertyId: ScalarId, visitId: ScalarId) =>
  generatePath(routes.visit, { propertyId: String(propertyId), visitId: String(visitId) });

export const toVisitEditPage = (propertyId: ScalarId, visitId: ScalarId) =>
  generatePath(routes.visitEdit, { propertyId: String(propertyId), visitId: String(visitId) });

export const toVisitCreatePage = (propertyId: ScalarId) =>
  generatePath(routes.visitCreate, { propertyId: String(propertyId) });

export const toCasePage = (propertyId: ScalarId, caseId: ScalarId) =>
  generatePath(routes.case, { propertyId: String(propertyId), caseId: String(caseId) });

export const toCaseCreatePage = (propertyId: ScalarId) =>
  generatePath(routes.caseCreate, { propertyId: String(propertyId) });

export const toNotePage = (propertyId: ScalarId, noteId: ScalarId) =>
  generatePath(routes.note, { propertyId: String(propertyId), noteId: String(noteId) });

export const toNoteCreatePage = (propertyId: ScalarId) =>
  generatePath(routes.noteCreate, { propertyId: String(propertyId) });

export const toHousingApplicationPage = (propertyId: ScalarId, applicationId: ScalarId) =>
  generatePath(routes.housingApplication, {
    propertyId: String(propertyId),
    applicationId: String(applicationId),
  });

export const toArrearsArrangementPage = (propertyId: ScalarId, arrangementId: ScalarId) =>
  generatePath(routes.arrearsArrangement, {
    propertyId: String(propertyId),
    arrangementId: String(arrangementId),
  });

export const toServiceRequestPage = (propertyId: ScalarId, serviceRequestId: ScalarId) =>
  generatePath(routes.serviceRequest, {
    propertyId: String(propertyId),
    serviceRequestId: String(serviceRequestId),
  });

// PCFRA

export const toPcrfaPropertyPage = (propertyId: ScalarId, personId?: ScalarId) =>
  generatePath(routes.pcfraProperty, { propertyId: String(propertyId) }) +
  (personId ? `?personId=${personId}` : '');

export const toPcrfaCreatePage = (propertyId: ScalarId, personId: ScalarId) =>
  generatePath(routes.pcfraCreate, { propertyId: String(propertyId), personId: String(personId) });

export const toPcrfaEditPage = (propertyId: ScalarId, personId: ScalarId, draftId: string) =>
  generatePath(routes.pcfraEdit, {
    propertyId: String(propertyId),
    personId: String(personId),
    draftId,
  });

// External

export const supportNeedsPersonPageUrl = (personId: number) =>
  `${supportNeedsBaseUrl}/people/${personId}?hvr=1`;
